import { type FormikContextType, type FormikErrors, getIn } from "formik";
import * as Yup from "yup";
import { isApiValidationError, mapApiErrorToFieldErrors } from "#/api/errors";

type GetFormHelperOptions = {
	helperText?: React.ReactNode;
	/**
	 * backendFieldName remaps the name in the form, for when it doesn't match the
	 * name used by the backend
	 */
	backendFieldName?: string;
	/**
	 * maxLength is used for showing helper text on fields that have a limited length,
	 * which will let the user know how much space they have left, or how much they are
	 * over the limit. Zero and negative values will be ignored.
	 */
	maxLength?: number;
	/**
	 * measureLength returns the length shown in the counter and compared with
	 * maxLength. Defaults to value.length, which counts UTF-16 code units.
	 */
	measureLength?: (value: string) => number;
};

export type FormHelpers = {
	name: string;
	onBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	id: string;
	value?: string | number;
	error: boolean;
	helperText?: React.ReactNode;
};

/**
 * The length counter appears once the measured length exceeds maxLength minus
 * this margin.
 */
const lengthCounterMargin = 30;

export const getFormHelpers =
	<TFormValues>(form: FormikContextType<TFormValues>, error?: unknown) =>
	(fieldName: string, options: GetFormHelperOptions = {}): FormHelpers => {
		const {
			backendFieldName,
			helperText: defaultHelperText,
			maxLength,
			measureLength = (value: string) => value.length,
		} = options;
		let helperText = defaultHelperText;
		const apiValidationErrors = isApiValidationError(error)
			? (mapApiErrorToFieldErrors(
					error.response.data,
				) as FormikErrors<TFormValues> & { [key: string]: string })
			: undefined;
		// Since the fieldName can be a path string like parameters[0].value we need to use getIn
		const touched = Boolean(getIn(form.touched, fieldName.toString()));
		const formError = getIn(form.errors, fieldName.toString());
		// Since the field in the form can be different from the backend, we need to
		// check for both when getting the error
		const apiField = backendFieldName ?? fieldName;
		const apiError = apiValidationErrors?.[apiField.toString()];

		const fieldProps = form.getFieldProps(fieldName);
		const value = fieldProps.value;

		let lengthError: React.ReactNode = null;
		// Show a message if the input is approaching or over the maximum length.
		if (maxLength && maxLength > 0 && typeof value === "string") {
			const length = measureLength(value);
			if (length > maxLength - lengthCounterMargin) {
				helperText = `This cannot be longer than ${maxLength} characters. (${length}/${maxLength})`;
				// Show it as an error, rather than a hint
				if (length > maxLength) {
					lengthError = helperText;
				}
			}
		}

		// API and regular validation errors should wait to be shown, but length errors should
		// be more responsive.
		const errorToDisplay =
			(touched && apiError) || lengthError || (touched && formError);

		return {
			...fieldProps,
			id: fieldName.toString(),
			error: Boolean(errorToDisplay),
			helperText: errorToDisplay || helperText,
		};
	};

export const onChangeTrimmed =
	<T>(form: FormikContextType<T>, callback?: (value: string) => void) =>
	(event: React.ChangeEvent<HTMLInputElement>): void => {
		event.target.value = event.target.value.trim();
		form.handleChange(event);
		callback?.(event.target.value);
	};

// REMARK: Keep these consts in sync with coderd/httpapi/httpapi.go
const maxLenName = 32;
const displayNameMaxLength = 64;
const usernameRE = /^[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*$/;
const displayNameRE = /^[^\s](.*[^\s])?$/;

// REMARK: see #1756 for name/username semantics
export const nameValidator = (
	name: string,
	{ maxLength = maxLenName }: { maxLength?: number } = {},
): Yup.StringSchema =>
	Yup.string()
		.required(`Please enter a ${name.toLowerCase()}.`)
		.matches(usernameRE, "Special characters (e.g.: !, @, #) are not supported")
		.max(maxLength, `${name} cannot be longer than ${maxLength} characters`);

export const displayNameValidator = (displayName: string): Yup.StringSchema =>
	Yup.string()
		.matches(
			displayNameRE,
			`${displayName} must start and end with non-whitespace character`,
		)
		.max(
			displayNameMaxLength,
			`${displayName} cannot be longer than ${displayNameMaxLength} characters`,
		)
		.optional();

export const iconValidator = Yup.string().label("Icon").max(256);

export const passwordManagerIgnoreProps = {
	autoComplete: "off",
	"data-1p-ignore": true,
	"data-lpignore": "true",
	"data-form-type": "other",
	"data-bwignore": true,
} as const;
